// TelcoTrack — VYKOP structural classification (dokop vs hlavní trasa) for branch-draw logic.
// Not an active map/KPI metric — UI filters use R100 / MET / UR (`vykop-segment-kind`).
// Dokop = branch VYKOP tied to BUDOVA (odbočka); same object type & workflow as main route.

import { normalizeObjectTypeForLookup } from './object-workflow/phase-codes';

export const VYKOP_VARIANT_IDS = {
  HLAVNI_TRASA: 'hlavni_trasa',
  DOKOP: 'dokop',
} as const;

export type VykopVariantId = (typeof VYKOP_VARIANT_IDS)[keyof typeof VYKOP_VARIANT_IDS];

export const VYKOP_VARIANT_IDS_LIST: readonly VykopVariantId[] = [
  VYKOP_VARIANT_IDS.HLAVNI_TRASA,
  VYKOP_VARIANT_IDS.DOKOP,
];

export const VYKOP_VARIANT_LABELS: Record<VykopVariantId, string> = {
  [VYKOP_VARIANT_IDS.HLAVNI_TRASA]: 'Hlavní trasa',
  [VYKOP_VARIANT_IDS.DOKOP]: 'Dokop',
};

/** Real object type stored in DB/API — table filters use {@link VYKOP_VARIANT_IDS} instead. */
export const VYKOP_OBJECT_TYPE = 'VYKOP' as const;

/**
 * @deprecated Structural filter options removed from UI — kept for legacy id detection
 * (`isVykopTypeFilterId`) when migrating saved prefs / URLs.
 */
export const VYKOP_TYPE_FILTER_OPTIONS: ReadonlyArray<{ value: VykopVariantId; label: string }> =
  VYKOP_VARIANT_IDS_LIST.map((id) => ({
    value: id,
    label: VYKOP_VARIANT_LABELS[id],
  }));

export function isVykopTypeFilterId(id: string): id is VykopVariantId {
  return (VYKOP_VARIANT_IDS_LIST as readonly string[]).includes(id);
}

/** Metadata keys set by BUDOVA branch-draw (`object-import.buildBranchSegmentDtos`). */
export const VYKOP_DOKOP_METADATA_KEYS = {
  branchParentBudovaId: 'branchParentBudovaId',
  source: 'source',
  branchDrawSource: 'budova-branch-draw',
} as const;

export interface VykopVariantLookup {
  type?: string | null;
  metadata?: unknown;
}

/** Object fields used to resolve linked BUDOVA for dokop VYKOP / branch SPOJKOVISTE. */
export interface BranchParentBudovaLookup extends VykopVariantLookup {
  parentObjectId?: string | null;
}

function readCustomFields(metadata: unknown): Record<string, unknown> | null {
  if (metadata == null || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  const m = metadata as Record<string, unknown>;
  const cf = m['customFields'] ?? m['custom_fields'];
  if (cf == null || typeof cf !== 'object' || Array.isArray(cf)) return null;
  return cf as Record<string, unknown>;
}

/** VYKOP segment created as BUDOVA odbočka (branch draw / dokop dependency). */
export function isDokopVykop(params: VykopVariantLookup): boolean {
  const type = normalizeObjectTypeForLookup(String(params.type ?? '').trim());
  if (type !== 'VYKOP') return false;

  const customFields = readCustomFields(params.metadata);
  const branchParent = String(customFields?.[VYKOP_DOKOP_METADATA_KEYS.branchParentBudovaId] ?? '').trim();
  if (branchParent.length > 0) return true;

  const source = String(customFields?.[VYKOP_DOKOP_METADATA_KEYS.source] ?? '').trim();
  return source === VYKOP_DOKOP_METADATA_KEYS.branchDrawSource;
}

/** Main-network VYKOP (not tied to BUDOVA branch draw). */
export function isHlavniTrasaVykop(params: VykopVariantLookup): boolean {
  const type = normalizeObjectTypeForLookup(String(params.type ?? '').trim());
  if (type !== 'VYKOP') return false;
  return !isDokopVykop(params);
}

export function getVykopVariantId(params: VykopVariantLookup): VykopVariantId | null {
  const type = normalizeObjectTypeForLookup(String(params.type ?? '').trim());
  if (type !== 'VYKOP') return null;
  return isDokopVykop(params) ? VYKOP_VARIANT_IDS.DOKOP : VYKOP_VARIANT_IDS.HLAVNI_TRASA;
}

/** Layer toggle: pass enabled variant ids (both on by default). Non-VYKOP always matches. */
export function matchesVykopVariantFilter(
  params: VykopVariantLookup,
  enabledVariants: ReadonlySet<string>,
): boolean {
  const variant = getVykopVariantId(params);
  if (!variant) return true;
  return enabledVariants.has(variant);
}

/**
 * Resolve linked BUDOVA id for dokop VYKOP or branch SPOJKOVISTE (odbočka).
 * Uses `metadata.customFields.branchParentBudovaId`, then `parentObjectId` when branch metadata/source matches.
 */
export function resolveBranchParentBudovaId(params: BranchParentBudovaLookup): string | null {
  const type = normalizeObjectTypeForLookup(String(params.type ?? '').trim());
  const customFields = readCustomFields(params.metadata);
  const fromMeta = String(customFields?.[VYKOP_DOKOP_METADATA_KEYS.branchParentBudovaId] ?? '').trim();
  if (fromMeta) return fromMeta;

  const parentId = String(params.parentObjectId ?? '').trim();
  if (!parentId) return null;

  if (type === 'VYKOP' && isDokopVykop(params)) return parentId;

  if (type === 'SPOJKOVISTE') {
    const source = String(customFields?.[VYKOP_DOKOP_METADATA_KEYS.source] ?? '').trim();
    if (source === VYKOP_DOKOP_METADATA_KEYS.branchDrawSource) return parentId;
  }

  return null;
}

/** True when object is dokop VYKOP or branch SPOJKOVISTE tied to a BUDOVA (metadata or parent id). */
export function isBranchLinkedToBudova(params: BranchParentBudovaLookup): boolean {
  return resolveBranchParentBudovaId(params) != null;
}

/** Junction SPOJKOVISTE created by BUDOVA branch-draw (shown on VYKOP map layers). */
export function isBranchJunctionSpojkoviste(params: BranchParentBudovaLookup): boolean {
  const type = normalizeObjectTypeForLookup(String(params.type ?? '').trim());
  if (type !== 'SPOJKOVISTE') return false;
  return isBranchLinkedToBudova(params);
}
